/**
 * EstimateDraftCardConnected — wrapper que conecta hooks de estimate outreach
 * al DraftCard shared. Billing Phase 1E. Trimmed: send + update + cancel +
 * reply — sin skip/regenerate/followup/schedule (fuera de scope de esta fase).
 */
import { useTranslation } from 'react-i18next';
import { DraftCard } from '../outreach/DraftCard.js';
import type { DraftCardLabels, NormalizedDraft } from '../outreach/types.js';
import {
  useSendEstimateDraft,
  useCancelEstimateDraft,
  useUpdateEstimateDraft,
  type EstimateOutreachDraft,
} from '../../hooks/useEstimateOutreach.js';

interface EstimateDraftCardConnectedProps {
  draft: EstimateOutreachDraft;
  estimateId: number;
}

export function EstimateDraftCardConnected({ draft, estimateId }: EstimateDraftCardConnectedProps) {
  const { t } = useTranslation();
  const send = useSendEstimateDraft(estimateId);
  const cancel = useCancelEstimateDraft(estimateId);
  const update = useUpdateEstimateDraft(estimateId);

  const normalized: NormalizedDraft = {
    id: draft.id,
    draftType: draft.draftType,
    subject: draft.subject,
    bodyHtml: draft.bodyHtml,
    bodyText: draft.bodyText,
    status: draft.status,
    sendError: draft.sendError,
    sentAt: draft.sentAt,
    createdAt: draft.createdAt,
    sequenceOrder: draft.sequenceOrder,
    sentMessageId: draft.sentMessageId,
  };

  const labels: DraftCardLabels = {
    initial: t('billing.outreach.draftType.estimateReview'),
    followupN: t('billing.outreach.draftType.estimateReview'),
    newEmail: t('billing.outreach.draftType.estimateReview'),
    briefRequest: '',
    contentPlanReview: '',
    designReview: '',
    estimateReview: t('billing.outreach.draftType.estimateReview'),
    replyType: t('billing.outreach.draftType.reply'),
    editBtn: t('billing.outreach.editBtn'),
    editSave: t('billing.outreach.editSave'),
    editCancel: t('common.cancel'),
    sendBtn: t('billing.outreach.sendBtn'),
    sendingLabel: t('billing.outreach.sending'),
    sendNow: t('billing.outreach.sendBtn'),
    cancelBtn: t('billing.outreach.cancelConfirm'),
    skipBtn: '',
    retryBtn: t('billing.outreach.retryBtn'),
    expandBtn: t('billing.outreach.expandBtn'),
    collapseBtn: t('billing.outreach.collapseBtn'),
    copyBtn: t('billing.outreach.copyBtn'),
    copied: t('billing.outreach.copied'),
    replyBtn: t('billing.outreach.reply'),
    sendReplyBtn: t('billing.outreach.sendReplyBtn'),
    sentAgo: t('billing.outreach.sentAgo', { time: '{time}' }),
    scheduledForLabel: '',
    editSubjectLabel: t('billing.outreach.editSubjectLabel'),
    editBodyLabel: t('billing.outreach.editBodyLabel'),
    actionFailed: t('billing.outreach.actionFailed'),
    composerPlaceholder: t('billing.outreach.composing'),
    failedStatus: t('billing.outreach.status.failed'),
    cancelledStatus: t('billing.outreach.status.cancelled'),
  };

  return (
    <DraftCard
      draft={normalized}
      labels={labels}
      useRichTextEditor={false}
      onSend={() => send.mutate(draft.id)}
      onCancel={() => cancel.mutate({ draftId: draft.id })}
      onUpdate={({ subject, bodyHtml, bodyText }) =>
        update.mutate({ draftId: draft.id, subject, bodyHtml, bodyText })
      }
      sendState={{ isPending: send.isPending, isError: send.isError, error: send.error }}
      cancelState={{ isPending: cancel.isPending, isError: cancel.isError, error: cancel.error }}
      updateState={{ isPending: update.isPending, isError: update.isError, error: update.error }}
    />
  );
}
